import { axios } from "..";
import { IEmailTemplate } from "../interfaces/models/IEmailTemplate";

interface IEmailTemplatesQuery {
  filter?: Record<string, any>;
  fields?: string[];
  sort?: { field: string; order: "asc" | "desc" };
  pagination?: { pageIndex: number; pageSize: number };
  includeDeleted?: boolean;
}

function buildEmailTemplateParams(query?: IEmailTemplatesQuery, includeDeleted?: boolean): URLSearchParams {
  const params = new URLSearchParams();
  if (includeDeleted) {
    params.append("includeDeleted", "true");
  }
  if (query?.filter && Object.keys(query.filter).length > 0) {
    params.append("filter", JSON.stringify(query.filter));
  }
  if (query?.fields && query.fields.length > 0) {
    params.append("fields", JSON.stringify(query.fields));
  }
  if (query?.sort) {
    params.append("sort", JSON.stringify(query.sort));
  }
  if (query?.pagination) {
    params.append("pagination", JSON.stringify(query.pagination));
  }
  return params;
}

/** Get email templates excluding soft-deleted ones (default for lists). */
export const getEmailTemplates = async (
  query?: IEmailTemplatesQuery,
): Promise<IEmailTemplate[]> => {
  try {
    const params = buildEmailTemplateParams(query, false);
    const queryString = params.toString();
    const path = queryString ? `./emailTemplate?${queryString}` : "./emailTemplate";
    const response = await axios.Get(path);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    return [];
  }
};

/** Get all email templates including soft-deleted ones (no isDeleted filter). */
export const getEmailTemplatesIncludeDeleted = async (
  query?: Omit<IEmailTemplatesQuery, "includeDeleted">,
): Promise<IEmailTemplate[]> => {
  try {
    const params = buildEmailTemplateParams(query, true);
    const queryString = params.toString();
    const path = queryString ? `./emailTemplate?${queryString}` : "./emailTemplate";
    const response = await axios.Get(path);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    return [];
  }
};

export const getEmailTemplateById = async (
  id: string,
  fields?: string[],
): Promise<IEmailTemplate | null> => {
  try {
    const params = new URLSearchParams();
    const filter: Record<string, unknown> = {
      id: { value: id, comparisonType: "EQUAL" },
    };
    params.append("filter", JSON.stringify(filter));
    if (fields?.length) {
      params.append("fields", JSON.stringify(fields));
    }
    const response = await axios.Get(`./emailTemplate?${params.toString()}`);
    return Array.isArray(response.data) ? response.data[0] : response.data;
  } catch (error) {
    return null;
  }
};

const DUPLICATE_SOURCE_FIELDS = [
  "id",
  "title",
  "category",
  "subject",
  "sender",
  "fromEmail",
  "emailType",
  "attachment",
  "template",
  "templateSource",
  "emailEventId",
  "beefreeJson",
] as const;

/** Full template body for duplicate (includes beefreeJson / HTML). */
export const getEmailTemplateByIdForDuplicate = async (
  id: string,
): Promise<IEmailTemplate | null> =>
  getEmailTemplateById(id, [...DUPLICATE_SOURCE_FIELDS]);

export const createEmailTemplate = async (emailTemplate: IEmailTemplate): Promise<IEmailTemplate> => {
  try {
    const response = await axios.Post("./emailTemplate", emailTemplate);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateEmailTemplate = async (emailTemplate: IEmailTemplate): Promise<IEmailTemplate> => {
  try {
    const response = await axios.Put(`./emailTemplate`, emailTemplate);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteEmailTemplate = async (id: string): Promise<any> => {
  try {
    const response = await axios.Delete(`./emailTemplate`, `${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const checkTemplateLinkedToScheduled = async (templateId: string): Promise<boolean> => {
  try {
    const response = await axios.Get(`./emailTemplate?checkScheduled=true&id=${encodeURIComponent(templateId)}`);
    return !!response?.data?.linkedToScheduled;
  } catch {
    return false;
  }
};

/** Page size for scanning titles; one round-trip until template count exceeds this. */
const EMAIL_TEMPLATE_TITLE_SCAN_PAGE_SIZE = 5000;

/**
 * Loads every template `title` as lowercase for client-side uniqueness checks.
 * Includes soft-deleted rows so this matches `checkForDuplicates` on the server
 * (which does not filter `isDeleted`).
 */
export async function fetchEmailTemplateTitlesLowercaseSet(): Promise<Set<string>> {
  const titleSet = new Set<string>();
  let pageIndex = 0;
  for (;;) {
    const batch = await getEmailTemplatesIncludeDeleted({
      fields: ["title"],
      pagination: { pageIndex, pageSize: EMAIL_TEMPLATE_TITLE_SCAN_PAGE_SIZE },
    });
    for (const t of batch) {
      if (t.title != null && String(t.title).trim() !== "") {
        titleSet.add(String(t.title).toLowerCase());
      }
    }
    if (batch.length < EMAIL_TEMPLATE_TITLE_SCAN_PAGE_SIZE) {
      break;
    }
    pageIndex += 1;
  }
  return titleSet;
}
